(ns frontend.ux-overhaul.listing.preview.view
  "The record preview of a listing: a panel at the right of the listing that shows the details of
   the record that the user clicked, for example a deal. It is the Drawer of ZapKit in the inline
   mode, as the record drawer of the UX mockup: a header with the buttons of the record (the menu of
   its actions, the button that opens its page and the button that closes the preview), the title
   of the record and a view-only card with its fields. The data is loaded by
   `frontend.ux-overhaul.listing.preview.event`."
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.opensearch.row :as os-row]
   [common.schema.listing-definition :as listing-definition]
   [common.schema.registry :as registry]
   [frontend.actions.entity :as entity-actions]
   [frontend.state.current-session :as session]
   [frontend.ux-overhaul.listing.preview.event :as event]
   [frontend.ux-overhaul.listing.preview.model :as model]
   [frontend.ux-overhaul.listing.preview.sub :as sub]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.drawer.view :as drawer]
   [ui.components.detail-card.view :as detail-card]
   [ui.components.icon-button.view :as icon-button]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.view.entity-creator.editor :as editor]
   [ui.view.entity-creator.model :as creator-m]
   [ui.view.entity-creator.utils :as creator-u]
   [ui.view.opensearch-cards.view :as cards]
   [ui.view.opensearch-value.currency :as currency]))

(def dom-overrides
  "Classes that the generated styles do not have. The card and its fields are in
   `ui.components.detail-card.view`.

   `:header-row` is the header row. It holds the Save button at the left and the record
   buttons at the right, as in the mockup. `:header-buttons`
   groups the buttons, 2 px apart as in the mockup. `:body` takes away the padding of the body of the
   Drawer, because `:content` has its own: 20 px at the sides and 16 px above and below, as in the
   mockup. `:content` stacks the title and the card. `:message` is the text of the loading and the
   error state. `:panel` is added to the panel of the Drawer. ZapKit gives the inline panel a fixed
   width of 520 px, and `max-w-full` lets it shrink to the width of a narrow listing, so that the
   buttons of the header stay in the window."
  {:header-row     "tw2-flex tw2-w-full tw2-items-center tw2-gap-3"
   :header-buttons "tw2-flex tw2-shrink-0 tw2-items-center tw2-gap-0.5"
   :body           "tw2-p-0"
   :content        "tw2-flex tw2-flex-col tw2-gap-4 tw2-px-5 tw2-py-4"
   :message        "tw2-text-body-md tw2-text-tertiary"
   :panel          "tw2-max-w-full"})

(defn open-event
  "The event that opens the preview of `record` in the listing `listing-id`, or nil when the
   record has no id. `record` is a card of the Kanban or a row of the grid. `accessible-fields` are
   the fields that the user can read, from the context of the listing. The function reads the
   schema of the tenant and makes a new request id, so it must run in a view or in a callback of a
   view, and not in an event."
  [{:keys [listing-id entity-type pipeline-id accessible-fields record]}]
  (when-let [entity-id (os-row/entity-id record)]
    (let [tenant-schema @session/tenant-schema
          accessible-field-paths (vec (keep (comp model/normalize-field-path :field-path)
                                            accessible-fields))
          base-config (model/card-config
                       {:kanban-card-config     (listing-definition/card-config entity-type :kanban)
                        :accessible-field-paths accessible-field-paths
                        :field-schema-fn        (fn [attribute]
                                                  (registry/get-field-schema tenant-schema attribute))})
          card-config (assoc base-config
                             :currency-fields
                             (currency/card-currency-field-paths
                              {:card-config            base-config
                               :tenant-schema          tenant-schema
                               :accessible-field-paths accessible-field-paths}))]
      [::event/open {:listing-id  listing-id
                     :entity-id   entity-id
                     :entity-type entity-type
                     :pipeline-id pipeline-id
                     :card-config card-config
                     :request-id  (random-uuid)}])))

(defn actions-hiccup
  "The menu of the actions of the record, for example Put on hold: the three dots button that opens
   the menu. `actions` is a vector of menu items, as `card-actions-fn` of the pipeline listings gives
   them for the card of the record. Nothing when there is no action."
  [actions]
  (when (seq actions)
    [dropdown-menu/view
     {:items   actions
      :props   {:hide-arrow? true
                :align       :end}
      :trigger [icon-button/view
                {:tag        :span
                 :aria-label (loc/tr :record-preview-actions)
                 :title      (loc/tr :record-preview-actions)}
                [icons/ellipsis-vertical-icon {:class (icon-button/icon-class :md)}]]}]))

(defn header-save-button
  "The Save button of the editable preview, at the left of the header as in the mockup.
   The button is off while there is no edit, while an edited field has a validation
   error, and while a save is in progress. `save` is nil for a read-only record, so no
   button is shown."
  [{:keys [disabled? saving? on-save]}]
  [button/sm-primary-solid
   {:disabled? disabled?
    :on-click  (fn [_]
                 (when-not disabled?
                   (on-save)))}
   (if saving?
     (loc/tr :record-preview-saving)
     (loc/tr :record-preview-save))])

(defn header-hiccup
  "The content of the header, as in the mockup: the Save button of an editable record at the
   left, and at the right the menu of the actions of the record, the button that opens the page
   of the record, and the button that closes the preview. `save` is nil for a read-only record.
   `on-open-page` is nil when the listing has no page for the record. Then the button is not
   shown. `actions` are the menu items, see `actions-hiccup`."
  [{:keys [on-close on-open-page actions save]}]
  (into [:div {:class (str (:header-row dom-overrides)
                           (if save
                             " tw2-justify-between"
                             " tw2-justify-end"))}]
        (concat
         (when save
           [[header-save-button save]])
         [[:div {:class (:header-buttons dom-overrides)}
           (actions-hiccup actions)
           (when (fn? on-open-page)
             [icon-button/view
              {:aria-label (loc/tr :record-preview-open-page)
               :title      (loc/tr :record-preview-open-page)
               :on-click   (fn [_]
                             (on-open-page))}
              [icons/external-link-icon {:class (icon-button/icon-class :md)}]])
           [icon-button/view
            {:aria-label (loc/tr :record-preview-close)
             :title      (loc/tr :record-preview-close)
             :on-click   (fn [_]
                           (when (fn? on-close)
                             (on-close)))}
            [icons/chevron-right-icon {:class (icon-button/icon-class :md)}]]]])))

(defn rows
  "The rows of the details: a vector of maps with `:key`, `:label` and `:value`. A field of the
   compiled `card-config` has a row when the record has a value for it, or when the field is in
   `pinned-fields`. The value of a pinned field that is empty is nil."
  [{:keys [card card-config pinned-fields]}]
  (let [pinned (set pinned-fields)]
    (->> (:body-fields card-config)
         (keep (fn [field-info]
                 (let [row (cards/body-row card field-info)]
                   (when (or (:value row)
                             (contains? pinned (:field-path field-info)))
                     row))))
         vec)))

(defn row-hiccup
  [{:keys [key label value]}]
  ^{:key (str key)}
  [detail-card/field
   {:label label
    :value value}])

(defn reload-failed?
  "True when `error`, the error of the state, is the failure of a reload of a record that the
   panel still shows."
  [error]
  (= :reload-failed (:cause error)))

(defn body-hiccup
  "The body of the preview for the state of the record. `state` is the map in the state of the
   listing: `:status`, `:card`, `:card-config` and `:pinned-fields`. The loaded record has its title
   and a card with a row for each field, see `rows`. A record with no row shows a text."
  [{:keys [status card card-config pinned-fields error]}]
  (let [message (fn [attrs text]
                  [:div {:class (:content dom-overrides)}
                   [:p (merge {:class (:message dom-overrides)} attrs)
                    text]])]
    (case status
      :loading
      (message nil (loc/tr :record-preview-loading))

      :failed
      (message {:role "alert"} (loc/tr :record-preview-failed))

      :loaded
      (let [title (cards/title-value card card-config)
            field-rows (rows {:card          card
                              :card-config   card-config
                              :pinned-fields pinned-fields})]
        [:div {:class (:content dom-overrides)}
         (when (reload-failed? error)
           [:p {:class (:message dom-overrides)
                :role  "alert"}
            (loc/tr :record-preview-reload-failed)])
         (when title
           [drawer/title-view {} title])
         (if (seq field-rows)
           (into [detail-card/view {}]
                 (map row-hiccup)
                 field-rows)
           [:p {:class (:message dom-overrides)}
            (loc/tr :record-preview-empty)])])

      nil)))

(defn resolve-editable
  "The pipeline and the stage entities of the record, and the stages of the
   pipeline sorted by their order, from the session. The session holds the
   pipelines and the stages, which are configuration data, also for a new-grid
   account. The result is nil when the pipeline or the stage is not known, so the
   preview falls back to the read-only card."
  [record-entity]
  (let [pipeline (some-> (model/entity-pipeline-id record-entity)
                         session/get-entity)
        stage    (some-> (model/entity-stage-id record-entity)
                         session/get-entity)]
    (when (and pipeline stage)
      {:pipeline pipeline
       :stage    stage
       :stages   (->> (session/pipeline-stages pipeline)
                      (sort-by :common/ord))})))

(defn editable-context
  "The editor args and the pipeline context for the editable preview of a loaded
   pipeline record, or nil when the pipeline or the stage of the record is not
   known. The args are built once here, so the Save button in the header and the
   editor in the body use the same creator-editor state. The function reads the
   session, so it runs in a view."
  [{:keys [listing-id entity-type entity]}]
  (when-let [ctx (resolve-editable entity)]
    (let [args {:ident             (model/editor-ident {:listing-id listing-id
                                                        :entity-id  (:db/id entity)})
                :entity-type       entity-type
                :pipeline          (:pipeline ctx)
                :stage             (:stage ctx)
                :stages            (:stages ctx)
                :config-path       (model/editor-config-path {:entity-type entity-type
                                                             :pipeline    (:pipeline ctx)})
                :entity-edits-path creator-m/entity-edits-path}]
      (assoc ctx
             :listing-id  listing-id
             :entity-type entity-type
             :entity      entity
             :args        args))))

(defn- reset-editor-edits!
  "Drop the edits of the editor in place, so the save button goes off after a save.
   It re-initializes the creator state at the same ident, which empties the edits
   and keeps the editor mounted. It does not remount the editor, because a remount
   would race the clear of the old mount against the set up of the new one on the
   same ident."
  [{:keys [args entity]}]
  (rf/dispatch [creator-m/initialize (assoc args :entity entity)]))

(defn save-edits!
  "Save the edited fields of the record. The function reads the schema of the
   tenant, so it runs in a callback of the view, not in an event. It normalizes the
   edits like the entity creator does, then updates the attributes with instant
   indexing, so the listing refreshes on the completion of the search index. After
   the save it drops the edits of the editor in place, but only when the preview
   still shows the same opening. `open-token` is the token of the opening at save
   time. So a late answer of a preview that the user closed and reopened does not
   drop the new edits. An empty set of edits only drops the edits, with no request."
  [{:keys [listing-id entity-id entity-type edits args entity open-token]}]
  (let [entity-schema     (registry/get-schema @session/tenant-schema entity-type)
        normalized        (creator-u/normalize-entity-edits
                           {:entity-schema    entity-schema
                            :default-currency @session/default-currency}
                           edits)
        reset-if-current! (fn []
                            (let [state @(rf/subscribe [::sub/state listing-id])]
                              (when (and (= entity-id (:entity-id state))
                                         (= open-token (:open-token state)))
                                (reset-editor-edits! {:args args :entity entity}))))]
    (if (seq normalized)
      (do
        (rf/dispatch [::event/save-start listing-id])
        (entity-actions/*->entity-update!
         entity-id
         normalized
         (fn [_resp]
           (rf/dispatch [::event/save-ok {:listing-id listing-id
                                          :entity-id  entity-id
                                          :open-token open-token
                                          :saved      normalized}])
           (reset-if-current!))
         (fn [_err]
           (rf/dispatch [::event/save-error {:listing-id listing-id
                                             :entity-id  entity-id
                                             :open-token open-token
                                             :message    (loc/tr :record-preview-save-failed)}]))))
      (reset-editor-edits! {:args args :entity entity}))))

(defn save-button-props
  "The props of the Save button, from the current edits of the editor. It
   subscribes to the edits and the validations of the creator-editor state, so it
   runs in a view. `editable` is the result of `editable-context`. `:saving?` is the
   save-in-progress flag, and `:stale?` is true when a reload failed and the fields
   are out of date. `:reloading?` is true while a reload of the record entity is in
   progress. The button is off in these states, so a save of stale values and a
   second save cannot start. `:open-token` is the token of the current opening, for
   the save guard."
  [{:keys [listing-id entity-type entity args]} {:keys [saving? stale? reloading? open-token]}]
  (let [edits       @(rf/subscribe [creator-m/entity-edits args])
        validations @(rf/subscribe [creator-m/entity-validations
                                    (assoc args :entity entity)])
        dirty?      (boolean (seq edits))]
    {:disabled? (or saving?
                    stale?
                    reloading?
                    (not dirty?)
                    (model/invalid-edits? {:edits       edits
                                           :validations validations}))
     :saving?   saving?
     :on-save   (fn []
                  (save-edits! {:listing-id  listing-id
                                :entity-id   (:db/id entity)
                                :entity-type entity-type
                                :edits       edits
                                :args        args
                                :entity      entity
                                :open-token  open-token}))}))

(defn editable-content
  "The editable fields of the record, for a pipeline entity. It shows the fields of
   the pipeline stage configuration for the stage that the record is in, both the
   mandatory and the optional ones, and it marks the mandatory ones. The entity
   creator editor renders the fields inside the detail card, so the inputs, the
   option pickers and the mandatory mark are the same as in the creator, and the
   card matches the record drawer of the mockup. The Save button is in the header,
   see `header-save-button`. The fields are read-only while a save is in progress
   (`:saving?`), while a reload of the record entity is in progress (`:reloading?`),
   and while the record is out of date after a failed reload (`:stale?`), so no edit
   is lost and no stale value is saved. A stale record shows an alert. The creator
   state is set up on mount and cleared on unmount. `editable` is the result of
   `editable-context`."
  [{:keys [entity-type entity pipeline args saving? stale? reloading?]}]
  (r/with-let [_ (rf/dispatch-sync [creator-m/initialize (assoc args :entity entity)])]
    (let [title      (not-empty (entity/display-string entity))
          read-only? (boolean (or saving? stale? reloading?))]
      [:div {:class (:content dom-overrides)}
       (when stale?
         [:p {:class (:message dom-overrides)
              :role  "alert"}
          (loc/tr :record-preview-reload-failed)])
       (when title
         [drawer/title-view {} title])
       [detail-card/view {}
        [editor/view
         args
         {:raw-config       (:pipeline/creator-config pipeline)
          :field-schemas    (registry/get-schema @session/tenant-schema entity-type)
          :entity           entity
          :force-read-only? read-only?}]]])
    (finally
      (rf/dispatch [creator-m/clear-state {:ident (:ident args)}]))))

(defn preview-body
  "The body of the preview. A pipeline entity gets the editable fields of its
   current stage once its entity is loaded. Every other entity, and a pipeline
   entity whose entity or stage cannot be resolved, gets the read-only card.
   `editable` is the result of `editable-context`, or nil. `stale?` is true when a
   reload failed and the fields are out of date. `reloading?` is true while a reload
   of the record entity is in progress. A first entity load that fails shows the
   read-only card with an alert, so the user knows why the fields are not editable."
  [{:keys [state editable stale? reloading?]}]
  (let [{:keys [entity-type entity-status saving?]} state]
    (cond
      (not (model/pipeline-entity-type? entity-type))
      (body-hiccup state)

      (= entity-status :loading)
      [:div {:class (:content dom-overrides)}
       [:p {:class (:message dom-overrides)}
        (loc/tr :record-preview-loading)]]

      editable
      ;; No key with an entity-id or a save-version: the Drawer is keyed by the
      ;; entity-id, so a switch of the record already remounts this. A remount on
      ;; a save would race the clear of the creator state against its set up on the
      ;; same ident, so the save clears the edits in place instead.
      [editable-content (assoc editable
                               :saving?    saving?
                               :stale?     stale?
                               :reloading? reloading?)]

      (= entity-status :failed)
      [:<>
       (when (= :loaded (:status state))
         [:div {:class (:content dom-overrides)}
          [:p {:class (:message dom-overrides)
               :role  "alert"}
           (loc/tr :record-preview-edit-load-failed)]])
       (body-hiccup state)]

      :else
      (body-hiccup state))))

(defn- escape-listener
  "A component with no content. It closes the preview on the key Esc while it is mounted."
  [{:keys [on-close]}]
  (r/with-let [handler (fn [e]
                         (dg/escape-key-handler e on-close))]
    (dg/add-event-listener! js/document "keydown" handler)
    nil
    (finally
      (dg/remove-event-listener! js/document "keydown" handler))))

(defn view
  "The preview panel of the listing `listing-id`. It renders nothing while the preview is closed.
   `:on-open-page` is a function of the id of the record that opens the page of the record, or nil.
   `:actions-fn` is a function of the card of the record that gives the menu items of its actions, or
   nil. The menu is there when the record is loaded and the function gives an item."
  [{:keys [listing-id on-open-page actions-fn]}]
  (let [state @(rf/subscribe [::sub/state listing-id])
        {:keys [entity-id entity-type entity entity-status saving?]} state
        stale? (some? (:entity-error state))
        reloading? (boolean (:reloading? state))
        editable (when (and (model/pipeline-entity-type? entity-type)
                            (= entity-status :loaded)
                            entity)
                   (editable-context {:listing-id  listing-id
                                      :entity-type entity-type
                                      :entity      entity}))
        save (when editable
               (save-button-props editable {:saving?     saving?
                                            :stale?      stale?
                                            :reloading?  reloading?
                                            :open-token  (:open-token state)}))
        close! (fn []
                 (rf/dispatch [::event/close listing-id]))]
    (when entity-id
      [:<>
       [escape-listener {:on-close close!}]
       ^{:key (str "record-preview-" entity-id)}
       [drawer/view
        {:visible?     true
         :mode         :inline
         :size         :md
         :class        (:panel dom-overrides)
         :aria-label   (loc/tr :record-preview-details)
         :body-class   (:body dom-overrides)
         :header-child (header-hiccup {:actions      (when (and (fn? actions-fn)
                                                                (:card state)
                                                                (not (reload-failed? (:error state))))
                                                       (actions-fn (:card state)))
                                       :on-close     close!
                                       :on-open-page (when (fn? on-open-page)
                                                       (fn []
                                                         (on-open-page entity-id)))
                                       :save         save})
         :body         [preview-body {:state      state
                                      :editable   editable
                                      :stale?     stale?
                                      :reloading? reloading?}]}]])))
